import React, { useContext, useState, useEffect, useRef } from 'react';
import { FiLogOut } from "react-icons/fi";
import ThemeToggle from "./ThemeToggle";
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import MenuMobile from './MenuMobile';
import { ThemeContext } from '../context/ThemeContext';
import ContactModal from './ContactModal';
import UserAvatar from './UserAvatar';
import SubscriptionStatusBanner from './SubscriptionStatusBanner';
import OnboardingProgressBanner from './OnboardingProgressBanner';
import { isViewerHome } from '../utils/access';
import { canAccessVistaContador } from '../utils/contadorAccess';
import { canAccessTeam } from '../utils/teamAccess';
import { needsSubscriptionPayment, getSubscriptionBlockedNavLinks } from '../utils/subscriptionGate';

const corePrivateLinks = [
  { key: 'clientes', label: 'Clientes', to: '/clientes' },
  { key: 'facturas', label: 'Registro de ventas', to: '/facturas' },
  { key: 'egresos', label: 'Egresos', to: '/egresos' },
  { key: 'reportes', label: 'Reportes', to: '/reportes' },
  { key: 'cobranzas', label: 'Cobrar', to: '/cobranzas' },
];

function getPrivateNavLinks(user) {
  if (needsSubscriptionPayment(user)) {
    return getSubscriptionBlockedNavLinks();
  }

  const contadorLink = canAccessVistaContador(user)
    ? [{ key: 'contador', label: 'Vista contador', to: '/contador' }]
    : [];

  if (isViewerHome(user)) {
    return [...contadorLink, ...corePrivateLinks];
  }
  return [
    { key: 'inicio', label: 'Inicio', to: '/inicio' },
    ...corePrivateLinks,
    ...contadorLink,
  ];
}

// Enlaces para usuarios no logueados (vistas públicas)
const publicLinks = [
  { key: 'funcionalidades', label: 'Funcionalidades', to: '/funcionalidades' },
  { key: 'planes', label: 'Planes', to: '/planes' },
  { key: 'quienes-somos', label: 'Quiénes Somos', to: '/quienes-somos' },
  { key: 'contacto', label: 'Contacto', to: '/contactos', isModal: true },
];

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const { modoOscuro, setModoOscuro } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [isFuncionalidadesHero, setIsFuncionalidadesHero] = useState(true); // Inicializar en true para empezar transparente
  const [isQuienesSomosHero, setIsQuienesSomosHero] = useState(true); // Inicializar en true para empezar transparente
  const [isLandingHero, setIsLandingHero] = useState(true); // Inicializar en true para empezar transparente
  const [isPlanesHero, setIsPlanesHero] = useState(true); // Inicializar en true para empezar transparente

  // Escuchar el evento para abrir el modal de contacto desde el menú mobile
  useEffect(() => {
    const handleOpenContactModal = () => {
      setContactModalOpen(true);
    };
    
    window.addEventListener('openContactModal', handleOpenContactModal);
    
    return () => {
      window.removeEventListener('openContactModal', handleOpenContactModal);
    };
  }, []);
  
  // Hooks de permisos
  // Detectar si estamos en la landing page, funcionalidades, quienes-somos o planes
  const isLandingPage = location.pathname === '/';
  const isFuncionalidades = location.pathname === '/funcionalidades';
  const isQuienesSomos = location.pathname === '/quienes-somos';
  const isPlanes = location.pathname === '/planes';

  // Ref para rastrear si es la primera carga de esta página
  const initialLoadRef = useRef(true);
  const prevPathRef = useRef(location.pathname);

  // Efecto para detectar scroll en páginas marketing
  useEffect(() => {
    // Detectar si cambiamos de ruta (nueva página)
    const isNewPage = prevPathRef.current !== location.pathname;
    prevPathRef.current = location.pathname;
    
    // Si es nueva página y estamos en marketing pages, forzar hero=true inicialmente
    if (isNewPage && (isFuncionalidades || isQuienesSomos || isLandingPage || isPlanes)) {
      initialLoadRef.current = true;
      if (isLandingPage) setIsLandingHero(true);
      if (isFuncionalidades) setIsFuncionalidadesHero(true);
      if (isQuienesSomos) setIsQuienesSomosHero(true);
      if (isPlanes) setIsPlanesHero(true);
    }

    // Resetear estados si no estamos en páginas marketing
    if (!isFuncionalidades && !isQuienesSomos && !isLandingPage && !isPlanes) {
      setIsFuncionalidadesHero(false);
      setIsQuienesSomosHero(false);
      setIsLandingHero(false);
      setIsPlanesHero(false);
      initialLoadRef.current = false;
      return;
    }

    let handleScroll;
    let container;
    let retryRaf = 0;

    const checkAndSetup = () => {
      container = document.getElementById('main-scroll');
      if (!container) {
        retryRaf = requestAnimationFrame(checkAndSetup);
        return;
      }

      // Handler de scroll - actualiza estado según posición
      handleScroll = () => {
        const isMobile = window.innerWidth < 1024;
        const desktopTopThreshold = 100;
        const scrollTop = container.scrollTop;
        
        const landingSection = window.__landingActiveSection;
        const funcionalidadesSection = window.__funcionalidadesActiveSection;
        const quienesSomosSection = window.__quienesSomosActiveSection;
        const planesSection = window.__planesActiveSection;
        
        if (isLandingPage) {
          const isHero = isMobile
            ? scrollTop === 0
            : scrollTop < desktopTopThreshold || landingSection == null || landingSection === 0;
          setIsLandingHero(isHero);
        }
        if (isFuncionalidades) {
          const isHero = isMobile
            ? scrollTop === 0
            : scrollTop < desktopTopThreshold || funcionalidadesSection == null || funcionalidadesSection === 0;
          setIsFuncionalidadesHero(isHero);
        }
        if (isQuienesSomos) {
          const isHero = isMobile
            ? scrollTop === 0
            : scrollTop < desktopTopThreshold || quienesSomosSection == null || quienesSomosSection === 0;
          setIsQuienesSomosHero(isHero);
        }
        if (isPlanes) {
          const isHero = isMobile
            ? scrollTop === 0
            : scrollTop < desktopTopThreshold || planesSection == null || planesSection === 0;
          setIsPlanesHero(isHero);
        }
      };

      // Configurar listener
      container.addEventListener('scroll', handleScroll);
      
      // Siempre verificar estado inicial inmediatamente
      handleScroll();
      
      // Marcar que ya no es carga inicial
      initialLoadRef.current = false;
    };

    // Ejecutar inmediatamente - el DOM ya debería estar listo
    checkAndSetup();

    return () => {
      cancelAnimationFrame(retryRaf);
      if (container && handleScroll) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isFuncionalidades, isQuienesSomos, isLandingPage, isPlanes, location.pathname]);

  // Estilos condicionales - transparente solo en el hero de landing, funcionalidades, quienes-somos y planes
  const isTransparent = (
    (isLandingPage && isLandingHero) ||
    (isFuncionalidades && isFuncionalidadesHero) ||
    (isQuienesSomos && isQuienesSomosHero) ||
    (isPlanes && isPlanesHero)
  );

  const isDarkMode = modoOscuro;
  const themeAppearance =
    isLandingPage && !isDarkMode ? 'default' : isDarkMode ? 'inverse' : 'default';

  const navBgClass = isTransparent
    ? isDarkMode
      ? 'bg-transparent text-white border-b border-white/20'
      : 'bg-transparent text-gray-900 border-b border-gray-200/50'
    : isDarkMode
      ? 'bg-black/80 backdrop-blur-lg text-white border-b border-white/10'
      : 'bg-white/95 backdrop-blur-lg text-gray-900 border-b border-gray-100';
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userHomePath = user
    ? (needsSubscriptionPayment(user)
      ? '/planes?pago=pendiente'
      : (isViewerHome(user) ? '/contador' : '/inicio'))
    : '/';
  const subscriptionBlocked = user && needsSubscriptionPayment(user);

  return (
    <>
    <nav className={`w-full flex items-center justify-between px-4 lg:px-10 py-4 font-sans transition-all duration-300 relative z-50 ${navBgClass}`}
      role="navigation"
      aria-label="Barra de navegación principal"
    >
      {/* Logo */}
      <div className="flex-shrink-0">
        <Link to={userHomePath} aria-label="Ir al inicio" className="flex items-center gap-2">
          <img
            src={isDarkMode ? "/logowhite.png" : "/logoblack.png"}
            alt="Logo"
            className="navbar-logo cursor-pointer select-none"
          />
        </Link>
      </div>

      {/* Menú principal (desktop) */}
      <div className="hidden lg:flex items-center gap-1.5">
        {(user ? getPrivateNavLinks(user) : publicLinks).map(link => (
          link.isModal ? (
            <button
              key={link.key}
              onClick={() => setContactModalOpen(true)}
              className={`px-3.5 py-2 rounded-full font-medium transition-colors text-sm
                ${isLandingPage || isFuncionalidades || isQuienesSomos || location.pathname === '/planes'
                  ? modoOscuro
                    ? 'text-white/90 hover:text-white hover:bg-white/20'
                    : 'text-black/90 hover:text-black hover:bg-black/20'
                  : isDarkMode
                    ? 'text-gray-300 hover:text-white hover:bg-white/10'
                    : 'text-gray-700 hover:text-black hover:bg-gray-100'
                }
              `}
            >
              {link.label}
            </button>
          ) : (
            <Link
              key={link.key}
              to={link.to}
              className={`px-3.5 py-2 rounded-full font-medium transition-colors text-sm
                ${isLandingPage || isFuncionalidades || isQuienesSomos || location.pathname === '/planes'
                  ? location.pathname.startsWith(link.to)
                    ? 'bg-white text-black'
                    : modoOscuro
                      ? 'text-white/90 hover:text-white hover:bg-white/20'
                      : 'text-black/90 hover:text-black hover:bg-black/20'
                  : location.pathname.startsWith(link.to)
                    ? (isDarkMode
                      ? 'bg-white text-black'
                      : 'bg-black text-white')
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-white/10'
                      : 'text-gray-700 hover:text-black hover:bg-gray-100'
                }
              `}
              aria-current={location.pathname.startsWith(link.to) ? 'page' : undefined}
            >
              {link.label}
            </Link>
          )
        ))}
        
        {/* Equipo (plan empresarial) — oculto si suscripción bloqueada */}
        {canAccessTeam(user) && !subscriptionBlocked && (
          <>
            <Link
              to="/equipo/usuarios"
              className={`px-3.5 py-2 rounded-full font-medium transition-colors text-sm
                ${isLandingPage || isFuncionalidades || isQuienesSomos || location.pathname === '/planes'
                  ? location.pathname.startsWith('/equipo')
                    ? 'bg-white text-black'
                    : modoOscuro
                      ? 'text-white/90 hover:text-white hover:bg-white/20'
                      : 'text-black/90 hover:text-black hover:bg-black/20'
                  : location.pathname.startsWith('/equipo')
                    ? (isDarkMode
                      ? 'bg-white text-black'
                      : 'bg-black text-white')
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-white/10'
                      : 'text-gray-700 hover:text-black hover:bg-gray-100'
                }
              `}
              aria-current={location.pathname.startsWith('/equipo') ? 'page' : undefined}
            >
              Equipo
            </Link>
          </>
        )}

        {/* Administración del sistema (solo superadmin) */}
        {user && user.role === 'admin' && user.isSuperAdmin === true && (
          <Link
            to="/admin/dashboard"
            className={`px-3.5 py-2 rounded-full font-medium transition-colors text-sm
              ${isLandingPage || isFuncionalidades || isQuienesSomos || location.pathname === '/planes'
                ? location.pathname.startsWith('/admin')
                  ? 'bg-white text-black'
                  : 'text-white/90 hover:text-white hover:bg-white/20'
                : location.pathname.startsWith('/admin')
                  ? (isDarkMode
                    ? 'bg-white text-black'
                    : 'bg-black text-white')
                  : isDarkMode
                    ? 'text-gray-300 hover:text-white hover:bg-white/10'
                    : 'text-gray-700 hover:text-black hover:bg-gray-100'
              }
            `}
            aria-current={location.pathname.startsWith('/admin') ? 'page' : undefined}
          >
            Administración
          </Link>
        )}
      </div>

      {/* Acciones a la derecha (desktop) */}
      <div className="hidden lg:flex items-center gap-3">
        {/* Botón de modo oscuro */}
        <ThemeToggle
          modoOscuro={modoOscuro}
          onToggle={setModoOscuro}
          appearance={themeAppearance}
          size="md"
        />

        {/* Usuario logueado */}
        {user ? (
          <>
            <Link
              to="/cuenta"
              className={`flex items-center gap-2.5 px-3.5 py-2 rounded-full transition-colors ${
                isLandingPage
                  ? 'hover:bg-white/20 text-white'
                  : isDarkMode
                    ? 'hover:bg-white/10'
                    : 'hover:bg-gray-50'
              }`}>
              <UserAvatar
                user={user}
                size="sm"
                className={isLandingPage ? 'bg-white text-black' : ''}
              />
              <span className={`font-medium ${
                isLandingPage
                  ? 'text-white'
                  : isDarkMode
                    ? 'text-white'
                    : 'text-gray-900'
              }`}>
                {user.name}
              </span>
            </Link>
            
            {/* Logout */}
            <button
              onClick={handleLogout}
              className={`p-2.5 rounded-full border transition-colors ${
                isLandingPage
                  ? isDarkMode
                    ? 'border-white/30 text-white hover:bg-white/20'
                    : 'border-gray-900/30 text-gray-900 hover:bg-gray-900/10'
                  : isDarkMode
                    ? 'border-white/20 text-white hover:bg-white/10'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-red-500'
              }`}
              title="Cerrar sesión"
            >
              <FiLogOut className="w-4 h-4" />
            </button>
          </>
        ) : (
          /* Botones de login/registro */
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className={`px-3.5 py-2 rounded-full font-medium text-sm transition-colors ${
                isLandingPage
                  ? isDarkMode
                    ? 'text-white hover:bg-white/20'
                    : 'text-gray-900 hover:bg-gray-900/10'
                  : isDarkMode
                    ? 'text-white hover:bg-white/10'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              Iniciar Sesión
            </Link>
          </div>
        )}
      </div>

      {/* Menú mobile */}
      <div className="flex items-center gap-2.5 lg:hidden shrink-0">
        <ThemeToggle
          modoOscuro={modoOscuro}
          onToggle={setModoOscuro}
          appearance={themeAppearance}
          size="md"
        />
        
        <button
          className={`p-3 rounded-xl transition-all duration-300 ${
            isDarkMode
              ? 'text-white hover:bg-white/10'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
          onClick={() => setMobileOpen(true)}
          aria-label="Abrir menú"
          aria-expanded={mobileOpen}
        >
          <div className="w-5 h-5 flex flex-col justify-center items-center gap-1">
            <div className={`w-5 h-0.5 bg-current transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-1.5' : ''}`}></div>
            <div className={`w-5 h-0.5 bg-current transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`}></div>
            <div className={`w-5 h-0.5 bg-current transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></div>
          </div>
        </button>
      </div>

      {/* Menú mobile tipo drawer */}
      <MenuMobile open={mobileOpen} onClose={() => setMobileOpen(false)} />
      
      {/* Contact Modal */}
      <ContactModal
        isOpen={contactModalOpen}
        onClose={() => {
          setContactModalOpen(false);
          if (location.pathname === '/contacto') {
            const main = document.getElementById('main-scroll');
            if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}
      />
    </nav>
    <OnboardingProgressBanner />
    <SubscriptionStatusBanner user={user} showWhenBlocked />
    </>
  );
};

export default Navbar; 